import React from "react";
import styled from "@emotion/styled";
import {
  Checkbox,
  TableCell,
  TableCellProps,
  Typography,
  useTheme,
} from "@mui/material";
import { useMandatorContext } from "@bn/theming";
import Arrow from "@icons/myicons/arrow-down-filled.svg?react";
import { GradientBox } from "./common";

const IconsWrapper = styled("span", {
  shouldForwardProp: (prop) => prop !== "sortDirection" && prop !== "side",
})<{ sortDirection: TableSortDirection; side?: "left" | "right" }>`
  display: inline-flex;
  vertical-align: middle;
  ${({ side }) =>
    side === "left" ? "margin-right: 4px;" : "margin-left: 4px;"}
  transform: ${({ sortDirection }) =>
    sortDirection === "ASC" ? "none" : "rotate(180deg)"};
  color: ${({ theme }) => theme.palette.icon.disabled};
`;

interface SortableIconProps {
  sortByName: string;
  sortDirection?: TableSortDirection;
  sortField?: string;
  side?: "left" | "right";
}

const ArrowUp = styled(Arrow)`
  color: ${({ theme }) => theme.palette.icon.secondary};
`;

const ArrowDown = styled(ArrowUp)`
  transform: rotate(180deg);
`;

const SortableIcon = ({
  sortDirection = "NONE",
  sortByName,
  sortField,
  side = "right",
}: SortableIconProps) => {
  const active = sortField === sortByName;

  return (
    <IconsWrapper
      sortDirection={sortDirection}
      side={side}
      style={{ visibility: active ? "visible" : "hidden" }}
    >
      <ArrowDown />
    </IconsWrapper>
  );
};

const SortableIconContainer = styled.div<{
  isClickable?: boolean;
  align?: TableCellProps["align"];
}>`
  display: inline;
  text-align: ${({ align }) => (align === "right" ? "right" : "left")};
`;

export interface SortableTableHeaderProps
  extends SortableIconProps,
    Omit<
      TableCellProps,
      "sortDirection" | "onClick" | "sortByName" | "sortField"
    > {
  text: string;
  isSortable?: boolean;
  onClick?: (sortByName: string) => void;
  isChecked?: boolean;
  handleCheckClick?: () => void;
  showGradient?: boolean;
}

export const SortableTableHeader = (props: SortableTableHeaderProps) => {
  const theme = useTheme();
  const {
    text,
    sortByName,
    sortField,
    sortDirection,
    isSortable = false,
    align,
    onClick = () => {},
    handleCheckClick,
    isChecked,
    showGradient = false,
    ...rest
  } = props;
  const { mode } = useMandatorContext();
  const active = sortField === sortByName;

  const handleClick = () => {
    if (!isSortable) return;
    onClick(sortByName);
  };

  return (
    <TableCell
      variant="head"
      onClick={handleClick}
      align={align}
      {...rest}
      sx={{
        ...props.sx,
        cursor: isSortable ? "pointer" : "auto",
        verticalAlign: "bottom",
        padding: theme.spacing(3.25, 3),
      }}
    >
      <SortableIconContainer isClickable={isSortable} align={align}>
        {handleCheckClick && (
          <Checkbox onClick={handleCheckClick} checked={isChecked} />
        )}
        <Typography variant="caption2" component="span">
          {(() => {
            const words = text.split(/\s+/);
            const icon = (
              <SortableIcon
                sortField={isSortable ? sortField : undefined}
                sortByName={sortByName}
                sortDirection={sortDirection}
                side={align === "right" ? "left" : "right"}
              />
            );
            // Keep the arrow on the side that does not push the visible text
            // off the cell's edge: left for right-aligned headers, right
            // otherwise. The arrow is glued (nowrap) to its adjacent word.
            if (align === "right") {
              const firstWord = words.shift() ?? "";
              const trailing = words.join(" ");
              return (
                <>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {icon}
                    {firstWord}
                  </span>
                  {trailing && ` ${trailing}`}
                </>
              );
            }
            const lastWord = words.pop() ?? "";
            const leading = words.join(" ");
            return (
              <>
                {leading && `${leading} `}
                <span style={{ whiteSpace: "nowrap" }}>
                  {lastWord}
                  {icon}
                </span>
              </>
            );
          })()}
        </Typography>
      </SortableIconContainer>
      {showGradient && <GradientBox mode={mode} />}
    </TableCell>
  );
};
